import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { FeedFormulaPerformance } from '../../../services/eggWatchAdminAPI';

interface Props {
  data: FeedFormulaPerformance[];
  compact?: boolean;
  onRowClick?: (formulaId: number) => void;
}

const EggWatchFeedPerformanceTable: React.FC<Props> = ({ data, compact, onRowClick }) => {
  const trendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUp size={14} />;
    if (trend === 'down') return <TrendingDown size={14} />;
    return <Minus size={14} />;
  };

  const rateClass = (rate: number) => {
    if (rate >= 75) return 'excellent';
    if (rate >= 55) return 'good';
    return 'low';
  };

  if (data.length === 0) {
    return <div className="ewa-empty-state">No feed performance data yet</div>;
  }

  if (compact) {
    return (
      <table className="ewa-mini-table">
        <thead>
          <tr>
            <th>Formula</th>
            <th>Farms</th>
            <th>Avg Lay Rate</th>
            <th>vs Expected</th>
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 5).map(f => (
            <tr
              key={f.formula_id}
              className={onRowClick ? 'clickable' : ''}
              onClick={() => onRowClick?.(f.formula_id)}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              <td>{f.formula_name}</td>
              <td>{f.farms_using}</td>
              <td className={`ewa-lay-rate ${rateClass(f.avg_lay_rate)}`}>
                {f.avg_lay_rate.toFixed(1)}%
              </td>
              <td className={f.vs_expected >= 0 ? 'good' : ''}>
                {f.vs_expected >= 0 ? '+' : ''}{f.vs_expected.toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="ewa-table-container">
      <table className="ewa-table">
        <thead>
          <tr>
            <th>Feed Formula</th>
            <th>Farms</th>
            <th>Hens</th>
            <th>Avg Lay Rate</th>
            <th>Median</th>
            <th>vs Expected</th>
            <th>Trend</th>
            <th>Heat Recovery</th>
            <th>Cold Recovery</th>
          </tr>
        </thead>
        <tbody>
          {data.map(f => (
            <tr
              key={f.formula_id}
              className={onRowClick ? 'clickable' : ''}
              onClick={() => onRowClick?.(f.formula_id)}
            >
              <td><strong>{f.formula_name}</strong></td>
              <td>{f.farms_using}</td>
              <td>{f.total_hens.toLocaleString()}</td>
              <td className={`ewa-lay-rate ${rateClass(f.avg_lay_rate)}`}>
                {f.avg_lay_rate.toFixed(1)}%
              </td>
              <td>{f.median_lay_rate.toFixed(1)}%</td>
              <td>
                <span className={f.vs_expected >= 0 ? 'ewa-positive' : 'ewa-negative'}>
                  {f.vs_expected >= 0 ? '+' : ''}{f.vs_expected.toFixed(1)}%
                </span>
              </td>
              <td>
                <span className={`ewa-trend ${f.trend}`}>
                  {trendIcon(f.trend)} {f.trend}
                </span>
              </td>
              <td>{f.heat_recovery_days != null ? `${f.heat_recovery_days}d` : '—'}</td>
              <td>{f.cold_recovery_days != null ? `${f.cold_recovery_days}d` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EggWatchFeedPerformanceTable;
